/**
 * COGS calculator — always a click away, seeded from what the window observed.
 *
 * It models cost the way the invoices do, not the way the ledger averages:
 *   carrier   per talk-minute, rounded UP to the carrier's pulse (60s), with
 *             a per-call minimum;
 *   STT       per REQUEST — one per caller turn — billed at the provider's
 *             minimum (Groq: 10 seconds) however short the utterance;
 *   LLM       per turn (prompt + reply), the unit the model bill scales with;
 *   TTS       per character, folded to a per-talk-minute figure;
 *   analysis  per call, once — the summariser does not run longer on a longer
 *             call, so it never appears in a per-minute figure.
 *
 * Every assumption starts as the measured figure for the current slice and
 * can be overridden. Arithmetic is in USD; display follows the page's
 * currency at the latest fixing, rupees to the paisa. Export gives
 * assumptions and results as one flat CSV.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { X, RotateCcw } from 'lucide-react';
import { UsageFilterState, UsageReportSummary } from '../../lib/api';
import { ExportCsv, downloadCsv, useCurrency } from './shared';

type PhoneA = {
  calls: number; connectRate: number; avgMinutes: number; turns: number; uttSec: number;
  llmPerTurn: number; ttsPm: number;
  sttRate: number; sttMin: number; sttInc: number;
  carrierRate: number; carrierPulse: number; carrierMin: number; carrierPerCall: number;
  attemptCost: number; analysis: number;
  pricePerMin: number; priceFixed: number;
};
type ScreenA = {
  sessions: number; avgMinutes: number; turns: number; uttSec: number;
  llmPerTurn: number; ttsPm: number; sttRate: number; sttMin: number; sttInc: number;
  analysis: number; price: number;
};
type MsgA = { messages: number; costPer: number; price: number };
type Assumptions = { phone: PhoneA; screen: ScreenA; messaging: MsgA; fxRate: number };

const STORE = 'usage.cogs.v2';
const n = (v: number | null | undefined, d = 0) => (v == null || Number.isNaN(v) ? d : v);

const windowDays = (f: UsageFilterState) =>
  Math.max(1, Math.round((new Date(f.to).getTime() - new Date(f.from).getTime()) / 86400000) + 1);

/** Billed seconds: rounded up to the increment, never below the minimum. */
export const billed = (seconds: number, inc: number, min: number) =>
  Math.max(Math.ceil(Math.max(0, seconds) / Math.max(1, inc)) * Math.max(1, inc), min);

/** The window's observed unit costs and pulses, volumes scaled to a 30-day month. */
export const observed = (summary: UsageReportSummary | null, filters: UsageFilterState): Assumptions => {
  const by = (ch: string) => summary?.by_channel.find((c) => c.channel === ch);
  const p = by('phone'); const u = by('ui'); const m = by('messaging');
  const scale = 30 / windowDays(filters);
  const funnel = summary?.phone_funnel;
  const connect = funnel && funnel.placed ? funnel.connected / funnel.placed : 1;
  const stt = summary?.pulses?.stt;
  const car = summary?.pulses?.carrier;
  const sttRate = n(stt?.per_minute_usd);
  const sttMin = n(stt?.minimum_seconds), sttInc = n(stt?.billing_increment_seconds, 1);
  return {
    phone: {
      calls: Math.round(n(p?.units) * scale / Math.max(connect, 0.01)),
      connectRate: Math.round(connect * 100) / 100,
      avgMinutes: n(p?.avg_minutes_per_unit, 1),
      turns: n(p?.turns_per_unit, 8),
      uttSec: n(p?.stt_seconds_per_request, 2.5),
      llmPerTurn: n(p?.llm_per_turn),
      ttsPm: n(p?.tts_per_minute),
      sttRate, sttMin, sttInc,
      carrierRate: n(car?.per_minute_usd, n(p?.telephony_per_minute)),
      carrierPulse: n(car?.billing_increment_seconds, 60),
      carrierMin: n(car?.minimum_seconds, 60),
      carrierPerCall: n(car?.per_call_usd),
      attemptCost: funnel && funnel.placed - funnel.connected > 0 ? funnel.unconnected_cost_usd / (funnel.placed - funnel.connected) : 0,
      analysis: n(p?.overhead_per_unit),
      pricePerMin: p && p.rated_minutes > 0 ? n(p.revenue_usd) / p.rated_minutes : 0,
      priceFixed: 0,
    },
    screen: {
      sessions: Math.round(n(u?.units) * scale),
      avgMinutes: n(u?.avg_minutes_per_unit, 0.5),
      turns: n(u?.turns_per_unit, 3),
      uttSec: n(u?.stt_seconds_per_request, 2.5),
      llmPerTurn: n(u?.llm_per_turn),
      ttsPm: n(u?.tts_per_minute),
      sttRate, sttMin, sttInc,
      analysis: n(u?.overhead_per_unit),
      price: n(u?.revenue_per_unit),
    },
    messaging: { messages: Math.round(n(m?.units) * scale), costPer: n(m?.cost_per_unit), price: n(m?.revenue_per_unit) },
    fxRate: n(summary?.fx?.rate, 0),
  };
};

type Unit = { carrier: number; llm: number; tts: number; stt: number; analysis: number; cogs: number; allin: number; perMin: number | null; billedMin: number; revenue: number };
type Line = Unit & { channel: string; units: number; attempts: number; minutes: number; monthly: { carrier: number; llm: number; tts: number; stt: number; analysis: number; cogs: number; revenue: number; margin: number } };

/** One connected call, the way it is invoiced. */
export const perCall = (a: PhoneA): Unit => {
  const talkSec = a.avgMinutes * 60;
  const billedMin = billed(talkSec, a.carrierPulse, a.carrierMin) / 60;
  const carrier = billedMin * a.carrierRate + a.carrierPerCall;
  const llm = a.turns * a.llmPerTurn;
  const tts = a.avgMinutes * a.ttsPm;
  const stt = a.turns * (billed(a.uttSec, a.sttInc, a.sttMin) / 60) * a.sttRate;
  const cogs = carrier + llm + tts + stt;
  const allin = cogs + a.analysis;
  const revenue = a.priceFixed + a.pricePerMin * billedMin;
  return { carrier, llm, tts, stt, analysis: a.analysis, cogs, allin, perMin: a.avgMinutes > 0 ? cogs / a.avgMinutes : null, billedMin, revenue };
};

export const perSession = (a: ScreenA): Unit => {
  const llm = a.turns * a.llmPerTurn;
  const tts = a.avgMinutes * a.ttsPm;
  const stt = a.turns * (billed(a.uttSec, a.sttInc, a.sttMin) / 60) * a.sttRate;
  const cogs = llm + tts + stt;
  return { carrier: 0, llm, tts, stt, analysis: a.analysis, cogs, allin: cogs + a.analysis, perMin: a.avgMinutes > 0 ? cogs / a.avgMinutes : null, billedMin: a.avgMinutes, revenue: a.price };
};

export const compute = (a: Assumptions): Line[] => {
  const pc = perCall(a.phone);
  const connected = a.phone.calls * a.phone.connectRate;
  const attempts = Math.max(0, a.phone.calls - connected);
  const mk = (channel: string, units: number, u: Unit, extraCarrier = 0, minutes = 0, attemptsN = 0): Line => {
    const monthly = {
      carrier: units * u.carrier + extraCarrier, llm: units * u.llm, tts: units * u.tts, stt: units * u.stt,
      analysis: units * u.analysis, cogs: 0, revenue: units * u.revenue, margin: 0,
    };
    monthly.cogs = monthly.carrier + monthly.llm + monthly.tts + monthly.stt + monthly.analysis;
    monthly.margin = monthly.revenue - monthly.cogs;
    return { ...u, channel, units, attempts: attemptsN, minutes, monthly };
  };
  const ps = perSession(a.screen);
  const msgUnit: Unit = { carrier: a.messaging.costPer, llm: 0, tts: 0, stt: 0, analysis: 0, cogs: a.messaging.costPer, allin: a.messaging.costPer, perMin: null, billedMin: 0, revenue: a.messaging.price };
  return [
    mk('phone', connected, pc, attempts * a.phone.attemptCost, connected * a.phone.avgMinutes, attempts),
    mk('ui', a.screen.sessions, ps, 0, a.screen.sessions * a.screen.avgMinutes),
    mk('messaging', a.messaging.messages, msgUnit),
  ];
};

const Num: React.FC<{ label: string; value: number; onChange: (v: number) => void; step?: number; suffix?: string; money?: boolean; hint?: string }> = ({ label, value, onChange, step, suffix, money, hint }) => {
  const { ccy, rate, symbol } = useCurrency();
  // Money is edited in the display currency (to the paisa) and stored in USD.
  const k = money && ccy === 'INR' && rate ? rate : 1;
  const dp = money ? (ccy === 'INR' ? 2 : 4) : 2;
  const shown = Math.round(value * k * 10 ** dp) / 10 ** dp;
  return (
    <label className="block" title={hint}>
      <span className="block truncate text-[10px] uppercase tracking-wide text-gray-400">{label}{money ? ` (${symbol})` : suffix ? ` (${suffix})` : ''}</span>
      <input
        type="number" step={step ?? (money ? 10 ** -dp : 'any')} value={Number.isFinite(shown) ? shown : 0}
        onChange={(e) => onChange((parseFloat(e.target.value) || 0) / k)}
        className="mt-0.5 w-full rounded border-gray-300 px-2 py-1 text-sm tabular-nums"
      />
    </label>
  );
};

const CHANNEL_NAME: Record<string, string> = { phone: 'Phone', ui: 'Screen', messaging: 'Messaging' };
const UNIT_NAME: Record<string, string> = { phone: 'call', ui: 'session', messaging: 'message' };

export const CogsCalculator: React.FC<{
  open: boolean;
  onClose: () => void;
  summary: UsageReportSummary | null;
  filters: UsageFilterState;
}> = ({ open, onClose, summary, filters }) => {
  const { fmt, toDisplay, ccy } = useCurrency();
  const seed = useMemo(() => observed(summary, filters), [summary, filters]);
  const [a, setA] = useState<Assumptions>(seed);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORE);
      if (saved) { setA({ ...seed, ...JSON.parse(saved) }); setTouched(true); return; }
    } catch { /* storage unavailable */ }
    setA(seed);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!touched) setA(seed); }, [seed, touched]);
  useEffect(() => {
    if (!touched) return;
    try { localStorage.setItem(STORE, JSON.stringify(a)); } catch { /* ignore */ }
  }, [a, touched]);

  const set = <K extends keyof Assumptions>(k: K, patch: Partial<Assumptions[K]>) => {
    setTouched(true);
    setA((prev) => ({ ...prev, [k]: { ...(prev[k] as object), ...patch } }));
  };
  const reset = () => { setTouched(false); setA(seed); try { localStorage.removeItem(STORE); } catch { /* ignore */ } };

  const lines = useMemo(() => compute(a), [a]);
  const total = lines.reduce((t, l) => ({
    cogs: t.cogs + l.monthly.cogs, revenue: t.revenue + l.monthly.revenue, llm: t.llm + l.monthly.llm, tts: t.tts + l.monthly.tts,
    stt: t.stt + l.monthly.stt, carrier: t.carrier + l.monthly.carrier, analysis: t.analysis + l.monthly.analysis,
  }), { cogs: 0, revenue: 0, llm: 0, tts: 0, stt: 0, carrier: 0, analysis: 0 });
  const phone = lines[0];
  const sttPulse = summary?.pulses?.stt;

  const exportCsv = () => {
    const d = (v: number | null) => (v == null ? '' : Math.round(toDisplay(v) * 100) / 100);
    const rows: Record<string, unknown>[] = [
      { section: 'assumption', item: 'currency', value: ccy },
      { section: 'assumption', item: 'fx_inr_per_usd', value: a.fxRate },
    ];
    const push = (prefix: string, obj: Record<string, number>, money: string[]) => {
      for (const [k, v] of Object.entries(obj)) rows.push({ section: 'assumption', item: `${prefix}.${k}`, value: money.includes(k) ? d(v) : v });
    };
    push('phone', a.phone as unknown as Record<string, number>, ['llmPerTurn', 'ttsPm', 'sttRate', 'carrierRate', 'carrierPerCall', 'attemptCost', 'analysis', 'pricePerMin', 'priceFixed']);
    push('screen', a.screen as unknown as Record<string, number>, ['llmPerTurn', 'ttsPm', 'sttRate', 'analysis', 'price']);
    push('messaging', a.messaging as unknown as Record<string, number>, ['costPer', 'price']);
    for (const l of lines) {
      const unit = UNIT_NAME[l.channel];
      for (const [k, v] of Object.entries({ carrier: l.carrier, llm: l.llm, tts: l.tts, stt: l.stt, analysis: l.analysis, cogs: l.cogs, allin: l.allin, cogs_per_minute: l.perMin, revenue: l.revenue })) {
        rows.push({ section: 'per_unit', item: `${l.channel}.per_${unit}.${k}`, value: d(v as number | null) });
      }
      rows.push({ section: 'monthly', item: `${l.channel}.units`, value: Math.round(l.units) },
        { section: 'monthly', item: `${l.channel}.minutes`, value: Math.round(l.minutes) });
      for (const [k, v] of Object.entries(l.monthly)) rows.push({ section: 'monthly', item: `${l.channel}.${k}`, value: d(v) });
    }
    rows.push({ section: 'monthly', item: 'total.cogs', value: d(total.cogs) }, { section: 'monthly', item: 'total.revenue', value: d(total.revenue) },
      { section: 'monthly', item: 'total.gross_margin', value: d(total.revenue - total.cogs) },
      { section: 'monthly', item: 'total.margin_pct', value: total.revenue ? Math.round(((total.revenue - total.cogs) / total.revenue) * 1000) / 10 : '' });
    downloadCsv(`cogs_monthly_${filters.from}_${filters.to}`, [
      { key: 'section', label: 'section' }, { key: 'item', label: 'item' }, { key: 'value', label: 'value' },
    ], rows);
  };

  if (!open) return null;
  const pctOf = (v: number) => (total.cogs ? `${((v / total.cogs) * 100).toFixed(0)}%` : '—');
  const sttBilled = billed(a.phone.uttSec, a.phone.sttInc, a.phone.sttMin);

  return (
    <div className="fixed inset-y-0 right-0 z-40 flex w-full max-w-[560px] flex-col border-l border-gray-200 bg-white shadow-2xl">
      <div className="flex items-start justify-between border-b border-gray-200 px-4 py-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">COGS calculator</div>
          <div className="text-[11px] text-gray-500">Costed the way the invoices bill: carrier pulses, STT minimums, LLM per turn. Seeded from {filters.from} → {filters.to}; edit anything.</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={reset} className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-50" title="Back to the window's observed figures">
            <RotateCcw className="h-3 w-3" /> Reset to observed
          </button>
          <ExportCsv onClick={exportCsv} title="Assumptions, per-unit and monthly results as one CSV" />
          <button onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="flex-1 space-y-5 overflow-y-auto px-4 py-4 text-sm">
        <section>
          <div className="mb-2 flex items-baseline justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">Phone · per connected call</span>
            <span className="text-xs tabular-nums text-gray-700">
              <strong className="text-gray-900">{fmt(phone.allin)}</strong> / call all-in · <strong className="text-gray-900">{fmt(phone.perMin)}</strong> / talk-min
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Num label="Calls / month" value={a.phone.calls} onChange={(v) => set('phone', { calls: v })} step={1} hint="Attempts placed" />
            <Num label="Connect rate" value={a.phone.connectRate * 100} onChange={(v) => set('phone', { connectRate: Math.min(100, Math.max(0, v)) / 100 })} step={1} suffix="%" hint="Share of placed calls that connect" />
            <Num label="Avg talk" value={a.phone.avgMinutes} onChange={(v) => set('phone', { avgMinutes: v })} step={0.1} suffix="min" />
            <Num label="Turns / call" value={a.phone.turns} onChange={(v) => set('phone', { turns: v })} step={0.5} hint="LLM turns; also the number of STT requests" />
            <Num label="Caller turn" value={a.phone.uttSec} onChange={(v) => set('phone', { uttSec: v })} step={0.5} suffix="s audio" hint="Average seconds of caller audio per turn — billed at the STT minimum when shorter" />
            <Num label="LLM / turn" value={a.phone.llmPerTurn} onChange={(v) => set('phone', { llmPerTurn: v })} money />
            <Num label="Speech out / talk-min" value={a.phone.ttsPm} onChange={(v) => set('phone', { ttsPm: v })} money hint="Per character, folded to a per-minute figure" />
            <Num label="STT rate / min" value={a.phone.sttRate} onChange={(v) => set('phone', { sttRate: v })} money hint={sttPulse ? `${sttPulse.provider}/${sttPulse.model}` : undefined} />
            <Num label="STT minimum" value={a.phone.sttMin} onChange={(v) => set('phone', { sttMin: v })} step={1} suffix="s / request" hint="Groq Whisper bills at least 10 seconds per request" />
            <Num label="Carrier / min" value={a.phone.carrierRate} onChange={(v) => set('phone', { carrierRate: v })} money hint={summary?.pulses?.carrier ? `${summary.pulses.carrier.provider} ${summary.pulses.carrier.direction} ${summary.pulses.carrier.country_code}: ${summary.pulses.carrier.currency} ${summary.pulses.carrier.per_minute}/min` : undefined} />
            <Num label="Carrier pulse" value={a.phone.carrierPulse} onChange={(v) => set('phone', { carrierPulse: v })} step={1} suffix="s" hint="Talk time is rounded UP to this" />
            <Num label="Carrier minimum" value={a.phone.carrierMin} onChange={(v) => set('phone', { carrierMin: v })} step={1} suffix="s / call" />
            <Num label="Unanswered attempt" value={a.phone.attemptCost} onChange={(v) => set('phone', { attemptCost: v })} money hint="What an attempt that never connects costs" />
            <Num label="Analysis / call" value={a.phone.analysis} onChange={(v) => set('phone', { analysis: v })} money hint="Per call, once. Never per minute." />
            <Num label="Price / min" value={a.phone.pricePerMin} onChange={(v) => set('phone', { pricePerMin: v })} money hint="What you charge per billed minute (seeded from the rate cards' effective price in this window)" />
            <Num label="Price fixed / call" value={a.phone.priceFixed} onChange={(v) => set('phone', { priceFixed: v })} money />
          </div>
          <div className="mt-2 rounded bg-amber-50 px-2.5 py-2 text-[11px] text-amber-900" title="How one connected call is costed">
            One call: carrier {fmt(phone.carrier)} ({phone.billedMin.toFixed(0)} billed min) · LLM {fmt(phone.llm)} ({a.phone.turns} turns) · speech out {fmt(phone.tts)} · STT {fmt(phone.stt)} ({a.phone.turns} × {sttBilled}s) · analysis {fmt(phone.analysis)} = <strong>{fmt(phone.allin)}</strong>
          </div>
        </section>

        <section>
          <div className="mb-2 flex items-baseline justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-sky-700">Screen · per session</span>
            <span className="text-xs tabular-nums text-gray-700"><strong className="text-gray-900">{fmt(lines[1].allin)}</strong> / session all-in</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Num label="Sessions / month" value={a.screen.sessions} onChange={(v) => set('screen', { sessions: v })} step={1} />
            <Num label="Avg engaged" value={a.screen.avgMinutes} onChange={(v) => set('screen', { avgMinutes: v })} step={0.1} suffix="min" />
            <Num label="Turns / session" value={a.screen.turns} onChange={(v) => set('screen', { turns: v })} step={0.5} />
            <Num label="LLM / turn" value={a.screen.llmPerTurn} onChange={(v) => set('screen', { llmPerTurn: v })} money />
            <Num label="Speech out / min" value={a.screen.ttsPm} onChange={(v) => set('screen', { ttsPm: v })} money />
            <Num label="Voice turn" value={a.screen.uttSec} onChange={(v) => set('screen', { uttSec: v })} step={0.5} suffix="s audio" hint="Seconds of mic audio per voice turn (0 for typed sessions)" />
            <Num label="STT rate / min" value={a.screen.sttRate} onChange={(v) => set('screen', { sttRate: v })} money />
            <Num label="Analysis / session" value={a.screen.analysis} onChange={(v) => set('screen', { analysis: v })} money />
            <Num label="Price / session" value={a.screen.price} onChange={(v) => set('screen', { price: v })} money />
          </div>
        </section>

        <section>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Messaging · per message</div>
          <div className="grid grid-cols-3 gap-2">
            <Num label="Messages / month" value={a.messaging.messages} onChange={(v) => set('messaging', { messages: v })} step={1} />
            <Num label="Cost / message" value={a.messaging.costPer} onChange={(v) => set('messaging', { costPer: v })} money />
            <Num label="Price / message" value={a.messaging.price} onChange={(v) => set('messaging', { price: v })} money />
          </div>
        </section>

        <section>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Monthly result</div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-gray-400">
                <th className="py-1 text-left">Channel</th>
                <th className="text-right">Units</th>
                <th className="text-right">COGS</th>
                <th className="text-right" title="All-in per connected call / session / message, analysis included">/ unit</th>
                <th className="text-right" title="Per talk- or engaged-minute, analysis excluded (it is per unit)">/ min</th>
                <th className="text-right">Revenue</th>
                <th className="text-right">Margin</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 tabular-nums">
              {lines.map((l) => (
                <tr key={l.channel}>
                  <td className="py-1.5 text-gray-800">{CHANNEL_NAME[l.channel]}{l.attempts > 0 && <span className="text-[10px] text-gray-400"> +{Math.round(l.attempts)} unanswered</span>}</td>
                  <td className="text-right text-gray-600">{Math.round(l.units).toLocaleString()}</td>
                  <td className="text-right font-medium text-gray-900">{fmt(l.monthly.cogs)}</td>
                  <td className="text-right text-gray-700">{fmt(l.allin)}</td>
                  <td className="text-right text-gray-700">{l.perMin == null ? '—' : fmt(l.perMin)}</td>
                  <td className="text-right text-gray-700">{fmt(l.monthly.revenue)}</td>
                  <td className={`text-right font-medium ${l.monthly.margin < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{fmt(l.monthly.margin)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t border-gray-200 font-semibold tabular-nums">
              <tr>
                <td className="py-1.5">Total</td>
                <td />
                <td className="text-right text-gray-900">{fmt(total.cogs)}</td>
                <td /><td />
                <td className="text-right text-gray-900">{fmt(total.revenue)}</td>
                <td className={`text-right ${total.revenue - total.cogs < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>
                  {fmt(total.revenue - total.cogs)}
                  {total.revenue > 0 && <span className="ml-1 text-[10px] font-normal text-gray-500">{(((total.revenue - total.cogs) / total.revenue) * 100).toFixed(0)}%</span>}
                </td>
              </tr>
            </tfoot>
          </table>
          <div className="mt-3 grid grid-cols-5 gap-2 text-[11px]">
            {([['LLM', total.llm], ['Speech out', total.tts], ['Speech in', total.stt], ['Carrier + msgs', total.carrier], ['Analysis', total.analysis]] as [string, number][]).map(([l, v]) => (
              <div key={l} className="rounded bg-gray-50 px-2 py-1.5">
                <div className="text-[10px] uppercase tracking-wide text-gray-400">{l}</div>
                <div className="font-medium tabular-nums text-gray-900">{fmt(v)}</div>
                <div className="text-gray-500">{pctOf(v)}</div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-gray-500">
            Arithmetic in USD; shown in {ccy}{ccy === 'INR' && a.fxRate ? ` at ₹${a.fxRate.toFixed(2)}/$ (latest ECB fixing), to the paisa` : ''}. Money inputs are typed in {ccy}.
          </p>
        </section>
      </div>
    </div>
  );
};
